import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Zap, Sparkles, TrendingUp, ShieldCheck, Search, Tags, Check, Crown, Star, Quote, Rocket, Clock, Users, ArrowRight, Flame } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAppSettings } from "@/hooks/useAppSettings";
import SeoHead from "@/components/SeoHead";
import TryItFreeModal from "@/components/TryItFree";
import { useState } from "react";

export default function Landing() {
  const { brand } = useAppSettings();
  const [tryOpen, setTryOpen] = useState(false);
  return (
    <div className="min-h-screen bg-gradient-subtle">
      <TryItFreeModal open={tryOpen} onOpenChange={setTryOpen} />
      <SeoHead />
      <header className="container mx-auto px-4 py-4 sm:py-6 flex items-center justify-between gap-2">
        <BrandMark size="sm" />
        <div className="flex gap-1 sm:gap-2 items-center">
          <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex"><a href="#pricing">Pricing</a></Button>
          <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex"><Link to="/about">About</Link></Button>
          <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex"><Link to="/auth">Sign In</Link></Button>
          <Button asChild size="sm" className="bg-gradient-primary shadow-glow"><Link to="/auth">Get Started</Link></Button>
          <div className="relative"><ThemeToggle /></div>
        </div>
      </header>

      <main className="container mx-auto px-4 pt-12 pb-24">
        <section className="relative text-center max-w-4xl mx-auto">
          {/* Decorative animated blobs */}
          <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none" aria-hidden>
            <div className="absolute top-0 left-1/4 w-72 h-72 bg-primary/30 rounded-full blur-3xl animate-blob" />
            <div className="absolute top-10 right-1/4 w-72 h-72 bg-accent/25 rounded-full blur-3xl animate-blob animation-delay-2000" />
            <div className="absolute -bottom-10 left-1/3 w-72 h-72 bg-primary-glow/30 rounded-full blur-3xl animate-blob animation-delay-4000" />
          </div>

          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-background/60 backdrop-blur px-4 py-1.5 text-xs font-medium mb-6 shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-primary animate-pulse" />
            <span className="text-gradient font-semibold">AI-powered SEO for e-commerce</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight mb-6 leading-[1.05]">
            <span className="block">Product listings that</span>
            <span className="block text-gradient-animated drop-shadow-[0_0_30px_hsl(var(--primary)/0.35)]">
              sell themselves.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
            Generate <span className="text-foreground font-semibold">SEO-optimized</span> titles, descriptions, keywords, and tags in seconds.
            Built for Shopify, WooCommerce, Amazon, eBay, Etsy, AliExpress, Alibaba, BigCommerce, Wix & every storefront in between.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={() => setTryOpen(true)} size="lg" className="bg-gradient-primary shadow-glow text-base hover:scale-105 transition-transform">
              See demo - no signup <Sparkles className="ml-1 h-4 w-4" />
            </Button>
            <Button asChild size="lg" variant="outline" className="text-base">
              <Link to="/auth">Signup - 5/day for free</Link>
            </Button>
          </div>
        </section>

        {/* Platforms supported */}
        <section className="mt-16 max-w-6xl mx-auto">
          <p className="text-center text-xs uppercase tracking-widest text-muted-foreground mb-5">
            Works with every major e-commerce platform
          </p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {[
              { name: "Shopify", slug: "shopify" },
              { name: "WooCommerce", slug: "woocommerce" },
              { name: "Amazon", slug: "amazon" },
              { name: "eBay", slug: "ebay" },
              { name: "Etsy", slug: "etsy" },
              { name: "AliExpress", slug: "aliexpress" },
              { name: "Alibaba", slug: "alibabadotcom" },
              { name: "BigCommerce", slug: "bigcommerce" },
              { name: "Wix", slug: "wix" },
              { name: "Squarespace", slug: "squarespace" },
              { name: "Magento", slug: "magento" },
              { name: "PrestaShop", slug: "prestashop" },
              { name: "Walmart", slug: "walmart" },
              { name: "Flipkart", slug: "flipkart" },
              { name: "Shopee", slug: "shopee" },
              { name: "Lazada", slug: "lazada" },
              { name: "Temu", slug: "temu" },
              { name: "OpenCart", slug: "opencart" },
              { name: "Ecwid", slug: "ecwid" },
              { name: "Rakuten", slug: "rakuten" },
              { name: "MercadoLibre", slug: "mercadopago" },
            ].map((p) => (
              <span
                key={p.name}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border/60 bg-background/70 backdrop-blur text-xs sm:text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 hover:shadow-sm transition-all"
              >
                <img
                  src={`https://cdn.simpleicons.org/${p.slug}`}
                  alt={`${p.name} logo`}
                  loading="lazy"
                  width={16}
                  height={16}
                  className="h-4 w-4 object-contain"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
                {p.name}
              </span>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-4">
            …and any other storefront. Our AI is platform-agnostic.
          </p>
        </section>

        {/* Before / After SEO comparison */}
        <section className="mt-20 max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Badge variant="secondary" className="mb-3">See the difference</Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
              Before vs <span className="text-gradient">After SEO</span>
            </h2>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              Real example of what our AI does to a typical product listing.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {/* BEFORE */}
            <Card className="p-5 sm:p-6 border-destructive/30 bg-destructive/5 relative overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <Badge variant="outline" className="border-destructive/40 text-destructive">Before</Badge>
                <span className="text-xs font-mono text-destructive">SEO score: 32/100</span>
              </div>
              <h3 className="font-semibold text-base mb-1 line-clamp-2">
                Blue Bottle 500ml
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Nice water bottle. Good quality. Buy now.
              </p>
              <div className="space-y-2 text-xs">
                <div className="flex gap-2 items-start">
                  <span className="text-muted-foreground w-20 shrink-0">Keywords:</span>
                  <span className="text-muted-foreground italic">- none -</span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-muted-foreground w-20 shrink-0">Tags:</span>
                  <span className="text-muted-foreground italic">bottle, blue</span>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-destructive/20 text-xs text-destructive flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-destructive animate-pulse" />
                Buried on page 7 of search results
              </div>
            </Card>

            {/* AFTER */}
            <Card className="p-5 sm:p-6 border-primary/40 bg-gradient-card shadow-glow relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-40 h-40 bg-primary/20 rounded-full blur-3xl pointer-events-none" aria-hidden />
              <div className="flex items-center justify-between mb-4 relative">
                <Badge className="bg-gradient-primary border-0 gap-1">
                  <Sparkles className="h-3 w-3" /> After
                </Badge>
                <span className="text-xs font-mono text-success font-semibold">SEO score: 94/100</span>
              </div>
              <h3 className="font-semibold text-base mb-1 relative">
                Insulated Stainless Steel Water Bottle 500ml - Leak-Proof, BPA-Free, Keeps Cold 24h
              </h3>
              <p className="text-sm text-muted-foreground mb-4 relative">
                Stay hydrated all day with our premium double-walled stainless steel bottle. Eco-friendly, fits car cup holders, and ideal for gym, hiking & travel.
              </p>
              <div className="space-y-2 text-xs relative">
                <div className="flex gap-2 items-start">
                  <span className="text-muted-foreground w-20 shrink-0">Keywords:</span>
                  <span className="flex flex-wrap gap-1">
                    {["insulated water bottle", "stainless steel bottle", "leak proof flask", "bpa free bottle", "gym water bottle"].map((k) => (
                      <span key={k} className="px-1.5 py-0.5 rounded bg-primary/10 text-primary">{k}</span>
                    ))}
                  </span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-muted-foreground w-20 shrink-0">Tags:</span>
                  <span className="flex flex-wrap gap-1">
                    {["hydration", "eco-friendly", "travel", "fitness", "outdoor"].map((t) => (
                      <span key={t} className="px-1.5 py-0.5 rounded bg-accent/20 text-accent-foreground">{t}</span>
                    ))}
                  </span>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-primary/20 text-xs text-success flex items-center gap-2 relative">
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
                Ranking on page 1 - 4.2× more clicks
              </div>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Button asChild size="lg" className="bg-gradient-primary shadow-glow">
              <Link to="/auth">Try it on your product →</Link>
            </Button>
          </div>
        </section>

        <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-16 sm:mt-24">
          {[
            { icon: Search, title: "SEO scoring", desc: "Every listing comes with a 0–100 quality score so you know it's ready to rank." },
            { icon: Tags, title: "Keywords + tags", desc: "10 high-intent keywords and 10 platform-ready tags per generation." },
            { icon: TrendingUp, title: "Built to convert", desc: "Trained on patterns that drive clicks, not just impressions." },
            { icon: ShieldCheck, title: "Secure backend", desc: "Your data and AI calls run through hardened serverless functions." },
            { icon: Sparkles, title: "Instant history", desc: "Every result is saved. Reuse, copy, or export anytime." },
            { icon: Zap, title: "Lightning fast", desc: "Powered by next-gen AI models with sub-3-second responses." },
          ].map((f) => (
            <Card key={f.title} className="p-6 bg-gradient-card border-border/50 hover:shadow-elegant transition-shadow">
              <div className="h-10 w-10 rounded-lg bg-gradient-primary/10 flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-semibold mb-1.5">{f.title}</h3>
              <p className="text-sm text-muted-foreground">{f.desc}</p>
            </Card>
          ))}
        </section>

        <section id="pricing" className="mt-24 scroll-mt-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">Simple, transparent pricing</h2>
            <p className="text-muted-foreground">Start free. Upgrade as you grow.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                name: "Free",
                price: "$0",
                period: "forever",
                desc: "Try it out, no card needed.",
                features: ["5 generations / day", "Powered by Gemini 3 Flash", "Basic SEO", "Limited history"],
                cta: "Start free",
                highlight: false,
              },
              {
                name: "Plus",
                price: "$9",
                period: "/month",
                desc: "For growing stores.",
                features: ["120 generations / month", "Powered by GPT-5 Mini", "Full history", "Export"],
                cta: "Upgrade",
                highlight: false,
                recommended: true,
              },
              {
                name: "Pro",
                price: "$19",
                period: "/month",
                desc: "For power users.",
                features: ["300 generations / month", "Powered by GPT-5 (top tier)", "Priority speed", "Advanced SEO (better prompts)", "Bulk generation", "Exports (CSV)"],
                cta: "Get Pro",
                highlight: true,
              },
            ].map((p: any) => {
              const isPro = p.highlight;
              const isPlus = p.recommended && !p.highlight;
              return (
                <div
                  key={p.name}
                  className="relative group transition-all duration-300 hover:-translate-y-1 h-full"
                >
                  {/* Animated gradient ring for highlighted plans */}
                  {(isPro || isPlus) && (
                    <div
                      className={`absolute -inset-[2px] rounded-xl bg-gradient-to-br ${
                        isPro
                          ? "from-primary via-primary/60 to-primary opacity-90"
                          : "from-primary/50 via-primary/20 to-primary/50 opacity-70"
                      } blur-[2px] group-hover:opacity-100 group-hover:blur-md transition-all duration-500`}
                      aria-hidden
                    />
                  )}

                  <Card
                    className={`relative p-7 flex flex-col h-full ${
                      isPro
                        ? "bg-gradient-card border-primary/50 shadow-glow"
                        : isPlus
                        ? "bg-gradient-card border-primary/30 shadow-elegant"
                        : "bg-gradient-card border-border/50 hover:border-border"
                    }`}
                  >
                    {/* Decorative corner glow for Pro (clipped via inner wrapper) */}
                    {isPro && (
                      <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none" aria-hidden>
                        <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/30 rounded-full blur-3xl" />
                      </div>
                    )}

                    {isPro && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-primary border-0 shadow-glow px-3 py-1 gap-1.5 z-20 whitespace-nowrap">
                        <Crown className="h-3.5 w-3.5" />
                        Most popular
                      </Badge>
                    )}
                    {isPlus && (
                      <Badge
                        variant="secondary"
                        className="absolute -top-3 left-1/2 -translate-x-1/2 border border-primary/40 px-3 py-1 gap-1.5 bg-background z-20 whitespace-nowrap"
                      >
                        <Star className="h-3.5 w-3.5 text-primary fill-primary" />
                        Recommended
                      </Badge>
                    )}

                    <div className="mb-5 relative">
                      <h3 className={`text-xl font-bold ${isPro ? "text-gradient" : ""}`}>{p.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{p.desc}</p>
                    </div>

                    <div className="mb-6 relative">
                      <div className="flex items-baseline gap-1">
                        <span className={`text-5xl font-extrabold tracking-tight ${isPro ? "text-gradient" : ""}`}>
                          {p.price}
                        </span>
                        <span className="text-muted-foreground text-sm">{p.period}</span>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-7 flex-1 relative">
                      {p.features.map((f: string) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm">
                          <div
                            className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${
                              isPro
                                ? "bg-gradient-primary shadow-glow"
                                : isPlus
                                ? "bg-primary/15"
                                : "bg-success/15"
                            }`}
                          >
                            <Check
                              className={`h-3 w-3 ${
                                isPro ? "text-primary-foreground" : isPlus ? "text-primary" : "text-success"
                              }`}
                              strokeWidth={3}
                            />
                          </div>
                          <span className="leading-relaxed">{f}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      asChild
                      size="lg"
                      className={`w-full relative font-semibold ${
                        isPro
                          ? "bg-gradient-primary shadow-glow hover:shadow-elegant"
                          : isPlus
                          ? "bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30"
                          : ""
                      }`}
                      variant={isPro ? "default" : isPlus ? "ghost" : "outline"}
                    >
                      <Link to="/auth">{p.cta}</Link>
                    </Button>
                  </Card>
                </div>
              );
            })}
          </div>
        </section>

        {/* Testimonials */}
        <section className="mt-24 max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-3 gap-1"><Flame className="h-3 w-3 text-orange-500" /> Loved by store owners</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Real results, real revenue</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { name: "Priya S.", role: "Shopify · Apparel", quote: "Our organic traffic jumped 3.4× in 6 weeks. The AI nails our brand voice.", stars: 5 },
              { name: "Marcus T.", role: "Amazon · Electronics", quote: "Used to spend hours per listing. Now 30 seconds and the SEO score is higher.", stars: 5 },
              { name: "Aïcha B.", role: "Etsy · Handmade", quote: "Tags & keywords I'd never have thought of. Conversions up 27% this month.", stars: 5 },
              { name: "Chen L.", role: "AliExpress · Gadgets", quote: "Optimized 200 listings in a weekend. Sales doubled within 3 weeks.", stars: 5 },
              { name: "Diego R.", role: "eBay · Collectibles", quote: "My listings finally rank. The keyword suggestions are gold.", stars: 5 },
              { name: "Sara K.", role: "WooCommerce · Beauty", quote: "Replaced our copywriter. Better output, fraction of the cost.", stars: 5 },
            ].map((t) => (
              <Card key={t.name} className="p-6 bg-gradient-card border-border/50 hover:shadow-elegant transition-all hover:-translate-y-1 relative">
                <Quote className="absolute top-4 right-4 h-6 w-6 text-primary/20" />
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.stars)].map((_, i) => (<Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />))}
                </div>
                <p className="text-sm leading-relaxed mb-4">"{t.quote}"</p>
                <div className="flex items-center gap-2.5 pt-3 border-t">
                  <div className="h-9 w-9 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground font-semibold text-sm">
                    {t.name[0]}
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.role}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="mt-24 max-w-5xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-primary p-8 sm:p-14 text-center shadow-glow">
            <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden>
              <div className="absolute top-0 left-1/4 w-72 h-72 bg-white/30 rounded-full blur-3xl animate-blob" />
              <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-accent/40 rounded-full blur-3xl animate-blob animation-delay-2000" />
            </div>
            <div className="relative">
              <Badge className="mb-4 bg-white/20 text-primary-foreground border-0 backdrop-blur gap-1">
                <Sparkles className="h-3 w-3" /> Limited launch pricing
              </Badge>
              <h2 className="text-3xl sm:text-5xl font-extrabold text-primary-foreground mb-4 leading-tight">
                Stop guessing. Start ranking.
              </h2>
              <p className="text-primary-foreground/90 text-base sm:text-lg max-w-2xl mx-auto mb-7">
                Join 2,400+ stores generating SEO that actually drives traffic - in seconds, not hours.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button asChild size="lg" variant="secondary" className="text-base font-semibold hover:scale-105 transition-transform">
                  <Link to="/auth">Start free <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="text-base bg-transparent border-white/40 text-primary-foreground hover:bg-white/10">
                  <a href="#pricing">View pricing</a>
                </Button>
              </div>
              <p className="text-primary-foreground/70 text-xs mt-5">No credit card required · 5 free generations daily</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t bg-background/50">
        <div className="container mx-auto px-4 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>© {new Date().getFullYear()} {brand.name || "StoreSEO AI"}. All rights reserved.</div>
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/about" className="hover:text-foreground">About</Link>
            <Link to="/terms" className="hover:text-foreground">Terms & Conditions</Link>
            <Link to="/refund-policy" className="hover:text-foreground">Refund Policy</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy Notice</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
